import TtlCache from '@isaacs/ttlcache';
import { Duration } from 'luxon';
import { ADMIN_PRINCIPAL_ID, AllowedPermissions, PrincipalType, UNKNOWN_ENUM_VALUE, } from '@lucidlink/dto/validation';
import { base64ToArray, cryptoPairsFromArray, decryptWithRsaBlockCipher, encryptionKeyPairFromArray, } from '@lucidlink/ts-crypto/crypto';
import { Resolver } from '@lucidlink/tscore';
import { prepareMemberCryptoArray } from '#sdk/common/member/prepare-member-common.ts';
import { prepareGroupCryptoArray } from '../group-manager/prepare-create-group-dto';
import { BatchedPrincipalsFetcher, } from './batched-principals-fetcher';
import { verifyWorkspaceCryptoTuple } from './crypto-utils';
import { InvalidCryptoError, PrincipalNotFoundError, PrincipalTypeMismatchError, } from './workspace-crypto.error';
const CACHE_TTL = Duration.fromObject({ minutes: 15 }).toMillis();
const PRINCIPALS_CACHE_MAX_SIZE = 1000;
const INVITATIONS_CACHE_MAX_SIZE = 500;
export class PrincipalsCrypto {
    workspaceId;
    adminCrypto;
    ensureMembersCrypto;
    ensureGroupsCrypto;
    logger;
    verifiedCryptoCache;
    /**
     * Raw principal records as returned by the lookup endpoint, keyed by principal
     * id. A `null` entry is a negative cache hit: WebService confirmed the principal
     * does not exist, so no further lookups are issued for it until the entry
     * expires or `refresh()` is called.
     */
    principalRecords;
    principalsFetcher;
    /**
     * Bumped by `refresh()`, so lookups started before it do not cache their,
     * possibly stale, results.
     */
    cacheGeneration = 0;
    constructor(workspaceId, adminCrypto, fetchPrincipalsByIds, ensureMembersCrypto, ensureGroupsCrypto, logger) {
        this.workspaceId = workspaceId;
        this.adminCrypto = adminCrypto;
        this.ensureMembersCrypto = ensureMembersCrypto;
        this.ensureGroupsCrypto = ensureGroupsCrypto;
        this.logger = logger;
        this.verifiedCryptoCache = createVerifiedCryptoCaches();
        this.principalRecords = new TtlCache({
            max: PRINCIPALS_CACHE_MAX_SIZE,
            ttl: CACHE_TTL,
        });
        this.principalsFetcher = new BatchedPrincipalsFetcher(async (principalIds) => this.ensureCryptoForRecords(await fetchPrincipalsByIds(principalIds)));
        this.logger = this.logger.child({ class: 'PrincipalsCrypto' });
    }
    async verifyAndGetPrincipalKeysSafe(principalId, params = {}) {
        return this.verifyAndGetPrincipalKeys(principalId, params).catch((error) => {
            const loggingParams = this.adminCrypto.getCurrentMemberForLogging({
                principalId,
                error,
            });
            // Expected for principals deleted while still referenced on the hub
            // (e.g. shares of a deleted group), so it is not worth an error.
            if (error instanceof PrincipalNotFoundError) {
                this.logger.debug(loggingParams, 'Principal not found');
            }
            else {
                this.logger.error(loggingParams, 'Could not verify principal keys');
            }
            return null;
        });
    }
    async verifyAndGetPrincipalKeys(principalId, { assertType } = {}) {
        const verifiedPrincipal = await this.getVerifiedPrincipal(principalId);
        if (assertType && assertType !== verifiedPrincipal?.type) {
            throw new PrincipalTypeMismatchError({
                principalId,
                expected: assertType,
                actual: verifiedPrincipal?.type,
            });
        }
        return {
            keys: verifiedPrincipal?.keys,
            allowedPermissions: this.parseAllowedPermissions(verifiedPrincipal),
        };
    }
    /**
     * Drops the cached principal records, including negative entries, and the
     * keyless verification results. Call it after principals were created, so a
     * previously missing id can be resolved again. Verified keys are kept.
     */
    refresh() {
        this.cacheGeneration++;
        this.principalRecords.clear();
        const keylessIds = [...this.verifiedCryptoCache.principals.entries()]
            .filter(([, verified]) => verified === null)
            .map(([principalId]) => principalId);
        for (const principalId of keylessIds) {
            this.verifiedCryptoCache.principals.delete(principalId);
        }
        this.principalsFetcher.detachInFlight();
    }
    /** Stops pending lookups, e.g. on logout, when the session token is no longer valid. */
    dispose() {
        this.principalsFetcher.cancel();
    }
    parseAllowedPermissions(verifiedPrincipal) {
        if (!verifiedPrincipal ||
            !verifiedPrincipal.allowedPermissions ||
            verifiedPrincipal.allowedPermissions === UNKNOWN_ENUM_VALUE) {
            return AllowedPermissions.NONE;
        }
        return verifiedPrincipal.allowedPermissions;
    }
    async getVerifiedPrincipal(principalId) {
        const cachedVerifiedPrincipal = this.verifiedCryptoCache.principals.get(principalId);
        if (cachedVerifiedPrincipal === null ||
            (cachedVerifiedPrincipal !== undefined &&
                cachedVerifiedPrincipal.keys.privateKey !== undefined)) {
            return cachedVerifiedPrincipal;
        }
        if (principalId === ADMIN_PRINCIPAL_ID) {
            const verifiedAdminPrincipal = await this.doVerifyAdminPrincipal();
            this.verifiedCryptoCache.principals.set(principalId, verifiedAdminPrincipal);
            return verifiedAdminPrincipal;
        }
        const cacheGeneration = this.cacheGeneration;
        const workspacePrincipal = await this.getWorkspacePrincipalOrThrow(principalId);
        const verifiedPrincipal = await this.doVerifyAndGetPrincipalKeys(workspacePrincipal);
        if (cacheGeneration === this.cacheGeneration) {
            this.verifiedCryptoCache.principals.set(principalId, verifiedPrincipal);
        }
        return verifiedPrincipal;
    }
    async doVerifyAdminPrincipal() {
        const adminKeys = await this.adminCrypto.verifyAndGetAdminKeys();
        return {
            type: PrincipalType.GROUP,
            keys: adminKeys.encryptPair,
            allowedPermissions: AllowedPermissions.READ_WRITE,
        };
    }
    async doVerifyAndGetPrincipalKeys(principal) {
        switch (principal.type) {
            case PrincipalType.GROUP:
                return this.doVerifyGroup(principal);
            case PrincipalType.MEMBER:
                return this.doVerifyMember(principal);
            case PrincipalType.MEMBER_WITHOUT_CRYPTO:
            case PrincipalType.GROUP_WITHOUT_CRYPTO: {
                // Crypto could not be ensured in the batch that fetched this record
                // (e.g. the principal was deleted meanwhile). There are no keys to verify.
                this.logger.warn({
                    principalId: principal.principalId,
                    type: principal.type,
                }, 'Principal has no crypto');
                return null;
            }
            case UNKNOWN_ENUM_VALUE: {
                this.logger.warn({ principal }, 'Unknown principal type');
                return null;
            }
        }
    }
    async doVerifyGroup(group) {
        const adminKeys = await this.adminCrypto.verifyAndGetAdminKeys();
        const groupPublicKeyArray = base64ToArray(group.publicKey);
        const isSignatureVerified = await verifyWorkspaceCryptoTuple({
            id: group.principalId,
            workspaceId: this.workspaceId,
            publicKey: groupPublicKeyArray,
        }, base64ToArray(group.sGroupByAdminKey), adminKeys.signPair.publicKey);
        if (!isSignatureVerified) {
            const reason = 'Could not verify group signature';
            this.logger.error(this.adminCrypto.getCurrentMemberForLogging({
                groupId: group.principalId,
            }), reason);
            throw new InvalidCryptoError(reason);
        }
        const groupPrivateKeyArray = await decryptWithRsaBlockCipher(group.ePrivateKeyByAdminKey, adminKeys.encryptPair.privateKey);
        const groupKeys = await encryptionKeyPairFromArray(groupPublicKeyArray, groupPrivateKeyArray);
        await this.adminCrypto.verifyPublicPrivateKeyPair(groupKeys, 'Group public and private key do not match', { groupId: group.principalId });
        return {
            keys: groupKeys,
            type: PrincipalType.GROUP,
            allowedPermissions: group.allowedPermissions,
        };
    }
    async doVerifyMember(member) {
        const invitationKeys = await this.verifyInvitation(member.invitation);
        return this.getPrincipalFromStoredRecord(member, invitationKeys);
    }
    async verifyInvitation(invitation) {
        let cachedInvitationKeys = this.verifiedCryptoCache.invitations.get(invitation.id);
        if (!cachedInvitationKeys) {
            cachedInvitationKeys = new Resolver();
            this.verifiedCryptoCache.invitations.set(invitation.id, cachedInvitationKeys);
            cachedInvitationKeys.resolve(this.doVerifyInvitation(invitation));
            // A failed verification must not stay cached, so the next lookup retries it.
            cachedInvitationKeys.promise.catch(() => {
                if (this.verifiedCryptoCache.invitations.get(invitation.id) ===
                    cachedInvitationKeys) {
                    this.verifiedCryptoCache.invitations.delete(invitation.id);
                }
            });
            return cachedInvitationKeys.promise;
        }
        return cachedInvitationKeys.promise;
    }
    async doVerifyInvitation(invitation) {
        const adminKeys = await this.adminCrypto.verifyAndGetAdminKeys();
        const invitationPublicKeyArray = base64ToArray(invitation.publicKey);
        const isVerified = await verifyWorkspaceCryptoTuple({
            id: invitation.id,
            workspaceId: this.workspaceId,
            publicKey: invitationPublicKeyArray,
        }, base64ToArray(invitation.sInvitationByAdminKey), adminKeys.signPair.publicKey);
        if (!isVerified) {
            const errorText = 'Could not verify invitation signature';
            this.logger.error(this.adminCrypto.getCurrentMemberForLogging({
                invitationId: invitation.id,
            }), errorText);
            throw new InvalidCryptoError(errorText);
        }
        const invitationPrivateKeyArray = await decryptWithRsaBlockCipher(invitation.ePrivateKeyByAdminKey, adminKeys.encryptPair.privateKey);
        const invitationKeys = await cryptoPairsFromArray(invitationPublicKeyArray, invitationPrivateKeyArray);
        await this.adminCrypto.verifyPublicPrivateKeyPair(invitationKeys.signPair, 'Invitation public and private keys do not match', {
            invitationId: invitation.id,
        });
        return invitationKeys;
    }
    async getPrincipalFromStoredRecord(member, invitationKeys) {
        const memberPublicKeyArray = base64ToArray(member.publicKey);
        const isSignatureVerified = await verifyWorkspaceCryptoTuple({
            id: member.principalId,
            workspaceId: this.workspaceId,
            publicKey: memberPublicKeyArray,
        }, base64ToArray(member.sMemberByInvitationKey), invitationKeys.signPair.publicKey);
        if (!isSignatureVerified) {
            const reason = 'Could not verify member signature';
            this.logger.error(this.adminCrypto.getCurrentMemberForLogging({
                memberId: member.principalId,
            }), reason);
            throw new InvalidCryptoError(reason);
        }
        const memberPrivateKeyArray = await decryptWithRsaBlockCipher(member.ePrivateKeyByInvitationKey, invitationKeys.encryptPair.privateKey);
        const memberKeys = await encryptionKeyPairFromArray(memberPublicKeyArray, memberPrivateKeyArray);
        await this.adminCrypto.verifyPublicPrivateKeyPair(memberKeys, 'Member public and private key do not match', { memberId: member.principalId });
        return {
            keys: memberKeys,
            type: PrincipalType.MEMBER,
            allowedPermissions: member.allowedPermissions,
        };
    }
    async getWorkspacePrincipalOrThrow(principalId) {
        let record = this.principalRecords.get(principalId);
        if (record === undefined) {
            const cacheGeneration = this.cacheGeneration;
            record = await this.principalsFetcher.resolve(principalId);
            if (cacheGeneration === this.cacheGeneration) {
                this.principalRecords.set(principalId, record);
            }
        }
        if (!record) {
            throw new PrincipalNotFoundError({ principalId });
        }
        return record;
    }
    /**
     * Principals created before crypto chains existed come back as `*_WITHOUT_CRYPTO`.
     * Their crypto is initialized here, once per lookup batch, so every record leaving
     * the fetcher carries keys whenever WebService could provide them.
     */
    async ensureCryptoForRecords(records) {
        const membersWithoutCrypto = records.filter((record) => record.type === PrincipalType.MEMBER_WITHOUT_CRYPTO);
        const groupsWithoutCrypto = records.filter((record) => record.type === PrincipalType.GROUP_WITHOUT_CRYPTO);
        if (!membersWithoutCrypto.length && !groupsWithoutCrypto.length) {
            return records;
        }
        // A failure fails the whole batch.
        const ensuredMembersPromise = this.ensureMembersCryptoForRecords(membersWithoutCrypto).catch((error) => {
            this.logger.error(this.adminCrypto.getCurrentMemberForLogging({ error }), 'Could not ensure members crypto');
            throw error;
        });
        const ensuredGroupsPromise = this.ensureGroupsCryptoForRecords(groupsWithoutCrypto).catch((error) => {
            this.logger.error(this.adminCrypto.getCurrentMemberForLogging({ error }), 'Could not ensure groups crypto');
            throw error;
        });
        const [ensuredMembers, ensuredGroups] = await Promise.all([
            ensuredMembersPromise,
            ensuredGroupsPromise,
        ]);
        const ensuredMembersById = new Map(ensuredMembers.map((member) => [member.principalId, member]));
        const ensuredGroupsById = new Map(ensuredGroups.map((group) => [group.principalId, group]));
        return records.map((record) => {
            if (record.type === PrincipalType.MEMBER_WITHOUT_CRYPTO) {
                const ensured = ensuredMembersById.get(record.principalId);
                return ensured
                    ? {
                        ...ensured,
                        allowedPermissions: pickAllowedPermissions(ensured, record),
                    }
                    : record;
            }
            if (record.type === PrincipalType.GROUP_WITHOUT_CRYPTO) {
                const ensured = ensuredGroupsById.get(record.principalId);
                return ensured
                    ? {
                        ...ensured,
                        allowedPermissions: pickAllowedPermissions(ensured, record),
                    }
                    : record;
            }
            return record;
        });
    }
    async ensureMembersCryptoForRecords(membersWithoutCrypto) {
        if (!membersWithoutCrypto.length) {
            return [];
        }
        const cryptoPrepareParamsList = await Promise.all(membersWithoutCrypto.map(async ({ principalId, invitation }) => ({
            workspaceId: this.workspaceId,
            invitationKeys: await this.verifyInvitation(invitation),
            principalId: principalId,
        })));
        const preparedCryptos = await prepareMemberCryptoArray(cryptoPrepareParamsList);
        return this.ensureMembersCrypto(preparedCryptos);
    }
    async ensureGroupsCryptoForRecords(groupsWithoutCrypto) {
        if (!groupsWithoutCrypto.length) {
            return [];
        }
        const groups = groupsWithoutCrypto.map((principal) => ({
            workspaceId: this.workspaceId,
            principalId: principal.principalId,
        }));
        const [ssoCrypto, adminKeys] = await Promise.all([
            this.adminCrypto.getSsoCrypto(),
            this.adminCrypto.verifyAndGetAdminKeys(),
        ]);
        const ssoGroupsKeys = await ssoCrypto.verifyAndGetSsoGroupKeys();
        const preparedCryptos = await prepareGroupCryptoArray(groups, adminKeys, ssoGroupsKeys);
        return this.ensureGroupsCrypto(preparedCryptos);
    }
}
const createVerifiedCryptoCaches = () => {
    return {
        principals: new TtlCache({
            max: PRINCIPALS_CACHE_MAX_SIZE,
            ttl: CACHE_TTL,
        }),
        invitations: new TtlCache({
            max: INVITATIONS_CACHE_MAX_SIZE,
            ttl: CACHE_TTL,
        }),
    };
};
/**
 * The crypto initialization endpoints return the crypto only, so the allowed
 * permissions are taken from the lookup record when they are missing there.
 * Without them WebFs refuses to grant the principal any permission.
 */
const pickAllowedPermissions = (ensured, record) => ensured.allowedPermissions === undefined ||
    ensured.allowedPermissions === UNKNOWN_ENUM_VALUE
    ? record.allowedPermissions
    : ensured.allowedPermissions;
